import React, { useEffect, useState } from "react";
import Select from "react-select";
import ls from "local-storage";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getSalesList,
  getProvincesBySMD,
  getCitiesBySMDAndProvince,
  getStoreById,
  getProvById,
  getCityById,
} from "src/services/SMD";
import { Typography } from "@mui/material";
import { CCol } from "@coreui/react";

const SalesPicker = ({ onFilterChange }) => {
  const [smd, setSmd] = useState(
    ls.get("smd")
      ? {
          id: ls.get("smd").id,
          label: ls.get("smd").label ? ls.get("smd").label : ls.get("smd").id,
          value: ls.get("smd").all ? ls.get("smd").all : ls.get("smd").id,
        }
      : {
          id: "999",
          label: "Not Loaded",
          value: "not",
        }
  );
  const [prov, setProv] = useState(
    ls.get("prov")
      ? {
          id: ls.get("prov").id,
          label: ls.get("prov").label
            ? ls.get("prov").label
            : ls.get("prov").id,
          value: ls.get("prov").all ? ls.get("prov").all : ls.get("prov").id,
        }
      : {
          id: "999",
          label: "Not Loaded",
          value: "not",
        }
  );
  const [city, setCity] = useState(
    ls.get("city")
      ? {
          id: ls.get("city").id,
          label: ls.get("city").label
            ? ls.get("city").label
            : ls.get("city").id,
          value: ls.get("city").all ? ls.get("city").all : ls.get("city").id,
        }
      : {
          id: "999",
          label: "Not Loaded",
          value: "not",
        }
  );

  const [smdOptions, setSmdOptions] = useState([]);
  const [provinceOptions, setProvinceOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();

  // Fetch SMD list
  const fetchSMDList = async () => {
    try {
      let resSMD = await getSalesList();

      if (resSMD.success === 1) {
        let data =
          resSMD?.data.map((item) => {
            return {
              id: item.id,
              label: `${item.name} - ${item.id}`,
              value: item.id,
            };
          }) ?? [];

        if (JSON.parse(localStorage.getItem("userDetail"))[0]?.role_id === 2) {
          data.unshift({
            id: "0",
            label: "All SMD",
            value: "all",
          });
        }

        if (smd.id === "999") {
          setSmd(data[0]);
        }

        setSmdOptions(data);
      }
    } catch (error) {
      console.error("Error fetching SMD list:", error);
    }
  };

  // Fetch provinces based on SMD selection
  const fetchProvinces = async (smdId) => {
    // if (!smdId || smdId === "0" || smdId === "all") {
    //   setProvinceOptions([]);
    //   setCityOptions([]);
    //   return;
    // }

    try {
      let resProv = await getProvincesBySMD(smdId);

      if (resProv.success === 1) {
        let data =
          resProv?.data.map((item) => {
            return {
              id: item.province_id,
              label: item.province_name,
              value: item.province_id,
            };
          }) ?? [];

        if (JSON.parse(localStorage.getItem("userDetail"))[0]?.role_id === 2) {
          data.unshift({
            id: "0",
            label: "All Provinces",
            value: "all",
          });
        }

        setProvinceOptions(data);
        setCityOptions([]); // Reset city options when SMD changes
      }
    } catch (error) {
      console.error("Error fetching provinces:", error);
    }
  };

  // Fetch cities based on SMD and Province selection
  const fetchCities = async (smdId, provId) => {
    // if (!smdId || smdId === "0" || smdId === "all" || !provId || provId === "0" || provId === "all") {
    //   setCityOptions([]);
    //   return;
    // }

    try {
      let resCity = await getCitiesBySMDAndProvince(smdId, provId);

      if (resCity.success === 1) {
        let data =
          resCity?.data.map((item) => {
            return {
              id: item.city_id,
              label: item.city_name,
              value: item.city_id,
            };
          }) ?? [];

        if (JSON.parse(localStorage.getItem("userDetail"))[0]?.role_id === 2) {
          data.unshift({
            id: "0",
            label: "All Cities",
            value: "all",
          });
        }

        setCityOptions(data);
      }
    } catch (error) {
      console.error("Error fetching cities:", error);
    }
  };

  // Initialize SMD list on component mount
  useEffect(() => {
    fetchSMDList();
  }, []);

  // Fetch provinces when SMD changes
  useEffect(() => {
    if (smd && smd.id !== "999") {
      fetchProvinces(smd.id);
    }
  }, [smd]);

  // Fetch cities when province changes
  useEffect(() => {
    if (prov && prov.id !== "999" && smd && smd.id !== "999") {
      fetchCities(smd.id, prov.id);
    }
  }, [prov, smd]);

  useEffect(() => {
    if (provinceOptions.length > 0 && smd && smd.id !== "999") {
      fetchCities(smd.id, provinceOptions[0].id);
    }
  }, [provinceOptions, smd]);

  // Handle SMD selection
  async function handleSMDChange(e) {
    if (e.value !== "all") {
      setSmd(e);

      const data = await getStoreById(e.id);
      ls.set("smd", { id: e.id, label: e.label, ...data?.data[0] } ?? {});
      
      // Reset province and city selections
      ls.set("prov", {
        id: 0,
        label: "All Provinces",
        value: "all",
      });
      ls.set("city", {
        id: 0,
        label: "All Cities",
        value: "all",
      });
      
      // Notify parent component about filter change
      if (onFilterChange) {
        onFilterChange();
      }
      
      navigate(0);
    } else {
      let data = e;
      data.value = "all";
      setSmd(data);
      ls.set("smd", data);
      
      // Reset province and city selections
      ls.set("prov", {
        id: 0,
        label: "All Provinces",
        value: "all",
      });
      ls.set("city", {
        id: 0,
        label: "All Cities",
        value: "all",
      });
      
      // Notify parent component about filter change
      if (onFilterChange) {
        onFilterChange();
      }
      
      navigate(0);
    }
  }

  // Handle Province selection
  async function handleProvChange(e) {
    if (e.value !== "all") {
      setProv(e);

      const data = await getProvById(e.id);
      console.log(data);
      ls.set("prov", { id: e.id, label: e.label, ...data?.data[0] } ?? {});
      
      // Reset city selection
      ls.set("city", {
        id: 0,
        label: "All Cities",
        value: "all",
      });
      
      // Notify parent component about filter change
      if (onFilterChange) {
        onFilterChange();
      }
      
      navigate(0);
    } else {
      let data = e;
      data.value = "all";
      setProv(data);
      ls.set("prov", data);
      
      // Reset city selection
      ls.set("city", {
        id: 0,
        label: "All Cities",
        value: "all",
      });
      
      // Notify parent component about filter change
      if (onFilterChange) {
        onFilterChange();
      }
      
      navigate(0);
    }
  }

  // Handle City selection
  async function handleCityChange(e) {
    if (e.value !== "all") {
      setCity(e);

      const data = await getCityById(e.id);
      ls.set("city", { id: e.id, label: e.label, ...data?.data[0] } ?? {});
      
      // Notify parent component about filter change
      if (onFilterChange) {
        onFilterChange();
      }
      
      navigate(0);
    } else {
      let data = e;
      data.value = "all";
      setCity(data);
      ls.set("city", data);
      
      // Notify parent component about filter change
      if (onFilterChange) {
        onFilterChange();
      }
      
      navigate(0);
    }
  }

  return (
    <>
      <div
        style={{
          display: "flex",
          gap: "10px",
          flexDirection: window.innerWidth > 768 ? "row" : "column",
        }}
      >
        <CCol style={{ marginBottom: "2vh", width: "100%", maxWidth: "300px" }}>
          <Typography>SMD Picker:</Typography>
          <div
            style={{
              width: "100%",
              zIndex: 99,
            }}
          >
            <Select
              placeholder="Pilih SMD"
              value={smd}
              options={smdOptions}
              onChange={(e) => {
                handleSMDChange(e);
              }}
              isDisabled={false}
            />
          </div>
        </CCol>
        <CCol style={{ marginBottom: "2vh", width: "100%", maxWidth: "300px" }}>
          <Typography>Province Picker:</Typography>
          <div
            style={{
              width: "100%",
              zIndex: 99,
            }}
          >
            <Select
              placeholder="Pilih Provinsi"
              value={prov}
              options={provinceOptions}
              onChange={(e) => {
                handleProvChange(e);
              }}
              isDisabled={!smd || smd.id === "999" || smd.value === "all"}
            />
          </div>
        </CCol>
        <CCol style={{ marginBottom: "2vh", width: "100%", maxWidth: "300px" }}>
          <Typography>City Picker:</Typography>
          <div
            style={{
              width: "100%",
              zIndex: 99,
            }}
          >
            <Select
              placeholder="Pilih Kota/Kabupaten"
              value={city}
              options={cityOptions}
              onChange={(e) => {
                handleCityChange(e);
              }}
              isDisabled={!prov || prov.id === "999" || prov.value === "all"}
            />
          </div>
        </CCol>
      </div>
    </>
  );
};

export default SalesPicker;
